import { css } from '@emotion/css';
import { Button, Collapse, Input, Modal, message } from 'antd';
import { MercadoItemFAQQuestion, answerTheQuestion, deleteQuestion } from './services';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway, useMemoizedFn, useRequest } from 'ahooks';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import useToggleShowTranslation from './useToggleShowTranslation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactComponent as AutoTranslateSvg } from 'views/Conversation/assets/autoTranslate.svg';
import Icon from '@ant-design/icons/lib/components/Icon';
import { PhrasesRootV2, PhrasesTextElement, PhraseAttachmentElement } from 'services/api/phrase';
import { PhraseValue, usePhraseOnChat } from 'hooks/business/usePhraseOnChat';
import ActivePopoverV2 from 'views/Conversation/Chat/components/Phrase/ActivePopoverV2';
import NewActivePopover from 'views/Conversation/Chat/components/Phrase/NewActivePopover';
import PassivePopover from 'views/Conversation/Chat/components/Phrase/PassivePopover';
import { noop } from 'lodash-es';
import { useReactRouterLoction } from 'views/Multitab/Router';
import { useBuyerFAQContext } from './BuyerFAQ';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

const MAX_LENGTH = 2000;

const collapseCls = css`
  width: 100%;
  flex: 1;
  .ant-collapse-item {
    padding-right: 12px;
    > .ant-collapse-header {
      padding-bottom: 0;
      .ant-collapse-arrow {
        margin-top: 6px;
      }
    }
    > .ant-collapse-content-box {
      padding-bottom: 0;
    }
  }
  & + & {
    .ant-collapse-header {
      .header {
        border-top: 1px dashed var(--fett-border-strong);
        padding-top: 12px;
      }
    }
  }
  textarea.ant-input {
    border: none;
  }
`;
interface TranslationButtonProps {
  loading: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const TranslationButton = (props: TranslationButtonProps) => {
  const { loading, onClick, disabled } = props;
  if (loading) {
    return <LoadingOutlined className="w-4 h-4 cursor-not-allowed" />;
  }
  return (
    <Icon
      component={AutoTranslateSvg}
      className="!text-primary cursor-pointer w-4 h-4 "
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick();
      }}
    />
  );
};
interface Props {
  preview?: boolean;
  questionItem: MercadoItemFAQQuestion;
  phraseData?: PhrasesRootV2;
  refreshList?: () => void;
  onChange: (faq?: MercadoItemFAQQuestion) => void;
}

const QuestionItem = (props: Props) => {
  const { preview, questionItem, onChange, refreshList } = props;
  const { expandable, region, storeId, userId } = questionItem;
  const [mode, setMode] = useState<'view' | 'input'>('view');
  const [inputText, setInputText] = useState('');
  const { t } = useTranslate();
  const lang = useSystemLanguageValue();
  const {
    text: question,
    onToggle: toggleQuestion,
    loading: questionTranslating,
  } = useToggleShowTranslation(questionItem.text, questionItem.textTranslate, lang);
  const {
    text: answer,
    onToggle: toggleAnswer,
    loading: answerTranslating,
  } = useToggleShowTranslation(questionItem.answer?.text, questionItem.answer?.textTranslate, lang);

  const submitAnswerSuccess = useMemoizedFn((value: string) => {
    onChange({
      ...questionItem,
      answer: {
        text: value,
        createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        status: 'active',
        userNick: '',
      },
    });
    setMode('view');
  });

  const { runAsync: onDeleteQuestion, loading: deleting } = useRequest(
    async () => {
      try {
        await deleteQuestion(questionItem.id);
        message.success(t('已删除'));
        refreshList?.();
      } catch (error) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    },
    {
      manual: true,
    },
  );
  const { runAsync: onBlockBuyer, loading: blocking } = useRequest(
    async () => {
      try {
        message.success(t('已屏蔽'));
      } catch (error) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    },
    { manual: true },
  );
  const { runAsync: submitAnswer, loading: submitting } = useRequest(
    () => answerTheQuestion(questionItem.id, inputText),
    {
      manual: true,
      onSuccess: () => {
        submitAnswerSuccess(inputText);
        message.success(t('已提交'));
      },
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );
  const { runAsync: translateInput, loading: inputTranslating } = useRequest(
    async () => {
      const result = await overseaTranslate(
        inputText,
        regionLanguageMap[toLowerCase(questionItem.region)],
        'messageEditor',
      );
      setInputText(() => result);
      return result;
    },
    {
      manual: true,
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );
  const { runAsync: translateAndAnswer, loading: translateAndAnswerLoading } = useRequest(
    async () => {
      const translateText = await translateInput();
      await answerTheQuestion(questionItem.id, translateText);
      submitAnswerSuccess(translateText);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success(t('已提交'));
      },
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );

  const onCollapse = useCallback(
    (key: any) => {
      const expandable = Boolean(key?.length);
      onChange({ ...questionItem, expandable });
    },
    [questionItem, onChange],
  );
  const {
    phraseGroups,
    phrasesByGroup,
    phraseFilter,
    isRelateRegion,
    updatePhraseFilter,
    phraseList,
  } = usePhraseOnChat('MERCADO', region, storeId);

  const onApplyPhrase = useMemoizedFn((phrase?: PhraseValue, append = true) => {
    if (!phrase) return;

    const { textEle, restEles } = phrase.phraseElems.elems.reduce(
      (res, ele: any) => {
        if (ele.attachment) {
          res.restEles.push(ele);
        } else if (ele.text) {
          if (res.textEle) {
            res.restEles.push(ele);
          } else {
            res.textEle = ele;
          }
        }
        return res;
      },
      {
        restEles: [],
      } as {
        textEle?: PhrasesTextElement;
        restEles: (PhrasesTextElement | PhraseAttachmentElement)[];
      },
    );

    if (textEle) {
      const content = textEle.text.translationContent || textEle.text.content;
      setInputText((text) => (append ? `${text}${content}` : content));
    }

    const restHasAttachment = restEles.some((ele: any) => ele.attachment);
    const restHasText = restEles.some((ele: any) => ele.text);

    if (restHasAttachment && restHasText) {
      message.warn(t('Mercado Q&A 回复不支持使用附件和多条消息'));
    } else if (restHasAttachment) {
      message.warn(t('Mercado Q&A 回复不支持使用附件'));
    } else if (restHasText) {
      message.warn(t('Mercado Q&A 回复不支持使用多条消息'));
    }
  });
  const [focus, setFocus] = useState(false);
  const contentClickRef = useRef(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  useClickAway(() => {
    setTimeout(() => {
      if (contentClickRef.current) {
        contentClickRef.current = false;
      } else {
        setFocus(false);
      }
    }, 200);
  }, triggerRef);
  const { pathname } = useReactRouterLoction();
  useEffect(() => {
    if (!pathname.startsWith('/workbench/mercado-faq')) {
      setFocus(false);
    }
  }, [pathname]);
  const { show } = useBuyerFAQContext();
  const { whiteListConfig: { NEW_PHRASE } = {} } = useOrgConfigStateValue();
  const ActivePopover = NEW_PHRASE ? NewActivePopover : ActivePopoverV2;
  return (
    <Collapse
      ghost
      expandIconPosition="right"
      className={collapseCls}
      activeKey={expandable || preview ? '1' : undefined}
      onChange={onCollapse}
      expandIcon={preview ? () => null : undefined}
    >
      <Collapse.Panel
        header={
          <div className="w-full flex gap-24 justify-between items-start header">
            <div className="flex items-start gap-2">
              <div className="h-5 leading-5 flex-none  flex justify-center items-center">
                <div className="text-primary border-primary border border-solid w-4 h-4 rounded-sm text-sm font-bold flex justify-center items-center">
                  Q
                </div>
              </div>
              <div className="flex-1 leading-5 flex gap-2 ">
                <span className="flex-1">{question}</span>
                <span className="flex-none text-[var(--fett-text-tertiary)]">
                  {dayjs(Number(questionItem.createTime)).format('YYYY-MM-DD HH:mm:ss')}
                </span>
                <span className="flex-none" onClick={(e) => e.stopPropagation()}>
                  <TranslationButton onClick={toggleQuestion} loading={questionTranslating} />
                </span>
              </div>
            </div>
            <div
              style={preview ? { display: 'none' } : undefined}
              className="flex-none"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[var(--fett-text-tertiary)] text-xs">
                {questionItem.userNick}({questionItem.userId})
              </span>
              <Button
                type="text"
                className="!p-0 !h-6 !text-xs ml-3 !hidden"
                onClick={() => {
                  Modal.confirm({
                    title: t('确认要屏蔽(x)吗？', { x: questionItem.userId }),
                    content: t('屏蔽后该买家将不可以再发起提问'),
                    onOk: onBlockBuyer,
                  });
                }}
                size="small"
                disabled={blocking}
              >
                {t('屏蔽买家')}
              </Button>
              <Button
                className="!p-0 !h-6 !text-xs ml-3"
                size="small"
                type="text"
                onClick={() => show({ storeId, buyerId: userId })}
              >
                {t('查看其他订单和Q&A')}
              </Button>
              <Button
                type="text"
                className="!p-0 !h-6 !text-xs ml-3"
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: t('确认要删除此问题吗？'),
                    content: t('删除后问题不可恢复'),
                    onOk: onDeleteQuestion,
                  });
                }}
                disabled={deleting}
              >
                {t('删除问题')}
              </Button>
            </div>
          </div>
        }
        key="1"
      >
        <div className="w-full flex gap-2 items-start">
          <div className="h-5 leading-5 flex-none  flex justify-center items-center">
            <div className="text-primary border-primary border border-solid w-4 h-4 rounded-sm text-sm font-bold flex justify-center items-center">
              A
            </div>
          </div>
          {mode === 'input' ? (
            <div className="flex-1">
              <div className="w-full border border-[var(--fett-border-strong)] rounded border-solid">
                <div className="p-2 border-0 border-b border-b-[var(--fett-border-default)] border-solid relative flex">
                  <ActivePopover
                    placement="topLeft"
                    channel="MERCADO"
                    mercadoQAmode
                    region={region}
                    conversationToggle={false}
                    phraseList={phraseList}
                    phraseFilter={phraseFilter}
                    phraseGroups={phraseGroups}
                    phrasesByGroup={phrasesByGroup}
                    updatePhraseFilter={updatePhraseFilter}
                    sendPhrase={onApplyPhrase}
                    onFillPhrasesText={noop}
                  />
                  <div style={{ marginLeft: '8px', fontSize: '18px' }}>
                    <TranslationButton
                      onClick={translateInput}
                      disabled={!inputText}
                      loading={inputTranslating}
                    />
                  </div>
                </div>
                <PassivePopover
                  mercadoQAmode
                  visible={focus}
                  placement="topLeft"
                  isRelateRegion={isRelateRegion}
                  region={region}
                  input={inputText}
                  setInput={noop}
                  onPhraseSelected={noop}
                  onPressEnter={(phrase) => onApplyPhrase(phrase, false)}
                  onFillPhraseText={noop}
                  onContentClick={() => {
                    setFocus(true);
                    contentClickRef.current = true;
                  }}
                >
                  <div ref={triggerRef}>
                    <Input.TextArea
                      className="!border-none"
                      maxLength={MAX_LENGTH}
                      showCount
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      rows={10}
                      autoSize={false}
                      onFocus={() => setFocus(true)}
                    />
                  </div>
                </PassivePopover>
              </div>

              <div className="flex gap-3 mt-3">
                <Button
                  type="primary"
                  size="small"
                  onClick={submitAnswer}
                  loading={submitting}
                  disabled={
                    !inputText || inputText.length > MAX_LENGTH || translateAndAnswerLoading
                  }
                >
                  {t('ViewStartups.reset.form.submit.btn')}
                </Button>
                <Button
                  size="small"
                  onClick={translateAndAnswer}
                  loading={translateAndAnswerLoading}
                  disabled={!inputText || inputText.length > MAX_LENGTH || submitting}
                >
                  {t('翻译后提交')}
                </Button>
                <Button
                  type="default"
                  size="small"
                  onClick={() => setMode('view')}
                  disabled={submitting}
                >
                  {t('取消')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-[var(--fett-text-tertiary)] leading-5 w-full">
              {questionItem?.answer?.text ? (
                <span className="w-full flex">
                  <span className="">{answer}</span>
                  <span className="flex-none mx-4 text-center">
                    <TranslationButton onClick={toggleAnswer} loading={answerTranslating} />
                  </span>
                </span>
              ) : (
                <span className="flex items-start gap-3">
                  <span className="h-5">{t('暂无回答')}</span>
                  {!preview && (
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      size="small"
                      className="!px-0 !h-5 -mt-0.5"
                      onClick={() => setMode('input')}
                    >
                      {t('添加回答')}
                    </Button>
                  )}
                </span>
              )}
              {questionItem.answer?.userNick ? (
                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 400 }}>
                  {t('回复人')}：{questionItem.answer.userNick}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

export default QuestionItem;
